/**
 * Racks section descriptor (#997 — Story 3.3).
 *
 * The real, live `SectionDescriptor` for the Racks (Rack Power) dynamic section.
 * It mirrors the synthetic Widget template but binds the ACTUAL Racks field set,
 * table, mapper, REST contract, and attachment cards, so the shared descriptor
 * engine can drive Racks at byte-identical parity with the pre-engine bespoke
 * implementation (Story 3.2 golden masters are the oracle).
 *
 * Field source of truth: `RACK_POWER_FIELDS` (`rack-power.fields.ts`) — every
 * per-unit field is typed here (text / multilineText / number / dropdown / yesNo)
 * to match the real controls, including the two BIT booleans
 * (`rackSeismicRated`, `rackRequiresLiquidToChip`). `rackName` is the engine's
 * instance name and `rackStatus` is the existing/new toggle; both are handled by
 * the engine list chrome, so they are NOT per-unit descriptor `fields` (they are
 * added back only on the Summary surface via the modal field slices).
 *
 * Additional Information (`rackAdditionalRackInformation`) is a PER-UNIT field on
 * `RackConfiguration`, NOT a shared/request-level field — so `sharedFieldKeys` is
 * `[]` and the adapter binds the Additional Info card to the per-unit instance
 * value.
 *
 * NFR7: the table (`request_racks`), mapper (`rack-configuration.mapper`), and
 * permission config are read-only; this descriptor only DECLARES the binding.
 */

import {
  formatRackTableCell,
  getRackColumnSubline,
  RACK_TABLE_COLUMN_ALIGN,
  rackColumnUsesDoubleLine,
  rackTableColumnsForLayout,
  rackTableHeaderLabel,
} from '../../components/racks/rackListUtils';
import {
  RACK_CONFIGURATIONS_LIST_TITLE,
  RACK_DELETE_CONFIRM_MESSAGE,
  RACK_DELETE_CONFIRM_TITLE,
  RACK_DISCARD_UNSAVED_DISCARD_LABEL,
  RACK_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
  RACK_DISCARD_UNSAVED_MESSAGE,
  RACK_DISCARD_UNSAVED_TITLE,
  RACK_EDIT_ALL_RACKS_LABEL,
  RACK_EDIT_DELETE_LABEL,
  RACK_EDIT_HEADER_TITLE_NEW,
  RACK_EDIT_SAVE_DRAFT_LABEL,
  RACK_EDIT_SAVE_LABEL,
  RACK_EMPTY_STATE_TITLE,
} from '../../constants/rackConfigurationsSubsectionCopy';
import { SECTION_IDS } from '../../constants/sectionIds';
import {
  RACK_COLOR_OPTIONS,
  RACK_MAX_LOAD_RATING_OPTIONS,
  RACK_PDU_MOUNTING_TYPE_OPTIONS,
  RACK_PDU_OUTLET_TYPE_OPTIONS,
  RACK_PLATFORM_OPTIONS,
} from '../../types/rack';
import { RACK_FORM_SUMMARY_FIELD_ORDER } from '../fields/requestFieldRegistry';
import { RACK_POWER_FIELDS } from '../fields/steps/rack-power.fields';

import type {
  SectionDescriptor,
  SectionDescriptorField,
  SectionDescriptorFieldType,
  SectionDescriptorFieldValidation,
  SectionListColumnsProjection,
} from './sectionDescriptor.types';
import type { RackTableColumnKey } from '../../components/racks/rackListUtils';
import type { RackConfiguration } from '../../types/rack';

/**
 * Per-field render type + validation + dropdown options, keyed by dataField.
 * Every `RACK_POWER_FIELDS` entry has an entry here; anything missing falls back
 * to `text`. Types match the real Racks controls (see `RackEditFormContent`).
 */
interface RackFieldTypeSpec {
  type: SectionDescriptorFieldType;
  options?: readonly string[];
  validation?: SectionDescriptorFieldValidation;
}

const RACK_FIELD_TYPES: Readonly<Record<string, RackFieldTypeSpec>> = {
  // Rack Information
  locationTagId: { type: 'locationTag' },
  rackPlatform: {
    type: 'dropdown',
    options: RACK_PLATFORM_OPTIONS,
    validation: { options: RACK_PLATFORM_OPTIONS },
  },
  rackDescription: { type: 'text' },
  rackUse: { type: 'text' },
  rackQuantity: { type: 'number', validation: { min: 0 } },
  rackRequiresLiquidToChip: { type: 'yesNo' },
  rackDescribeElevations: { type: 'multilineText' },
  // Specs
  rackSeismicRated: { type: 'yesNo' },
  rackMaxLoadRating: {
    type: 'dropdown',
    options: RACK_MAX_LOAD_RATING_OPTIONS,
    validation: { options: RACK_MAX_LOAD_RATING_OPTIONS },
  },
  rackColor: {
    type: 'dropdown',
    options: RACK_COLOR_OPTIONS,
    validation: { options: RACK_COLOR_OPTIONS },
  },
  rackDimensionsHeight: { type: 'number', validation: { min: 0 } },
  rackDimensionsLength: { type: 'number', validation: { min: 0 } },
  rackDimensionsWidth: { type: 'number', validation: { min: 0 } },
  // Rack Power Information (rackElectrical)
  rackPduManufacturer: { type: 'text' },
  rackPduModel: { type: 'text' },
  rackPduMaxKwPerRack: { type: 'text' },
  powerAverageItLoadPerRackKw: { type: 'text' },
  powerMaximumItLoadPerRackKw: { type: 'text' },
  powerNumberAndTypeInputPerRack: { type: 'text' },
  rackPduPowerType: { type: 'text' },
  rackPduInputPlugType: { type: 'text' },
  rackPduCountPerRack: { type: 'text' },
  rackPduDcPowerShelves: { type: 'number', validation: { min: 0 } },
  rackPduOutletSwitchingMonitored: {
    type: 'dropdown',
    options: RACK_PDU_OUTLET_TYPE_OPTIONS,
    validation: { options: RACK_PDU_OUTLET_TYPE_OPTIONS },
  },
  rackPduCapacityAmps: { type: 'number', validation: { min: 0 } },
  rackPduCapacityVolts: { type: 'number', validation: { min: 0 } },
  rackPduCapacityKw: { type: 'number', validation: { min: 0 } },
  rackPduLoadReadingsKw: { type: 'text' },
  rackPduMountingType: {
    type: 'dropdown',
    options: RACK_PDU_MOUNTING_TYPE_OPTIONS,
    validation: { options: RACK_PDU_MOUNTING_TYPE_OPTIONS },
  },
  rackPduOutletTypesQuantity: { type: 'text' },
  rackPduLocation: { type: 'text' },
  // Additional Information
  rackAdditionalRackInformation: { type: 'multilineText' },
};

/** Typed per-unit descriptor fields, one per `RACK_POWER_FIELDS` entry. */
const RACKS_DESCRIPTOR_FIELDS: readonly SectionDescriptorField[] =
  RACK_POWER_FIELDS.map(field => {
    const spec = RACK_FIELD_TYPES[field.dataField] ?? { type: 'text' };
    return {
      ...field,
      type: spec.type,
      options: spec.options,
      validation: spec.validation,
    };
  });

/**
 * Snake_case columns the Racks section persists — the exact key set emitted by
 * `rack-configuration.mapper.toEntity` and (excluding identity/audit columns) the
 * `request_racks` migration columns. The `descriptorDbBinding` guardrail joins
 * this list to both.
 */
export const RACKS_DESCRIPTOR_DB_COLUMNS: readonly string[] = [
  'request_id',
  'rack_status',
  'rack_name',
  'location_tag_id',
  'rack_platform',
  'rack_quantity',
  'rack_dimensions_length',
  'rack_dimensions_height',
  'rack_dimensions_width',
  'rack_additional_rack_information',
  'rack_description',
  'rack_use',
  'rack_seismic_rated',
  'rack_max_load_rating',
  'rack_color',
  'rack_requires_liquid_to_chip',
  'rack_describe_elevations',
  'rack_pdu_mounting_type',
  'rack_pdu_count_per_rack',
  'rack_pdu_outlet_switching_monitored',
  'rack_pdu_outlet_types_quantity',
  'rack_pdu_power_type',
  'rack_pdu_max_kw_per_rack',
  'rack_pdu_additional_info',
  'rack_pdu_manufacturer',
  'rack_pdu_model',
  'rack_pdu_input_plug_type',
  'rack_pdu_dc_power_shelves',
  'rack_pdu_capacity_amps',
  'rack_pdu_capacity_volts',
  'rack_pdu_capacity_kw',
  'rack_pdu_load_readings_kw',
  'rack_pdu_location',
  'display_order',
];

/**
 * Racks list projection (#997 Story 6.3 — parity by reuse). Delegates to the
 * product-approved bespoke table (`rackListUtils`: NAME | PLATFORM | QTY |
 * MAX KW | RPDU / PLUG + compact set, sublines, double-line cells). Engine rows
 * carry identity as `instanceName`/`instanceStatus` (stripped from the rack
 * shape by `rackToDescriptorInstance`), so adapt the row back before the
 * rack-shaped formatters run.
 */
const rackRowForList = (row: Record<string, unknown>): RackConfiguration => {
  const rack = row as unknown as RackConfiguration;
  return {
    ...rack,
    rackName:
      rack.rackName ?? (row.instanceName as RackConfiguration['rackName']),
    rackStatus:
      rack.rackStatus ??
      (row.instanceStatus as RackConfiguration['rackStatus']),
  };
};

const RACKS_LIST_COLUMNS: SectionListColumnsProjection = {
  locationTagCellColumn: 'ROOM',
  locationTagSublineColumn: 'NAME',
  columnsForLayout: rackTableColumnsForLayout,
  headerLabel: (column, layout) =>
    rackTableHeaderLabel(column as RackTableColumnKey, layout),
  formatCell: (row, column) =>
    formatRackTableCell(rackRowForList(row), column as RackTableColumnKey),
  columnAlign: RACK_TABLE_COLUMN_ALIGN,
  getColumnSubline: (row, column, layout) =>
    getRackColumnSubline(
      column as RackTableColumnKey,
      rackRowForList(row),
      layout,
    ),
  columnUsesDoubleLine: (column, layout) =>
    rackColumnUsesDoubleLine(column as RackTableColumnKey, layout),
};

/** The canonical Racks descriptor. */
export const RACKS_DESCRIPTOR: SectionDescriptor = {
  id: 'racks',
  stepId: 'rack-power',
  labels: {
    entitySingular: 'Rack',
    entityPlural: 'Racks',
    addButtonLabel: 'Add Rack',
    emptyStateTitle: RACK_EMPTY_STATE_TITLE,
    editFormTitle: RACK_EDIT_HEADER_TITLE_NEW,
    editFormSubtitle: RACK_CONFIGURATIONS_LIST_TITLE,
    // Chrome copy parity (#997 Story 6.3 R5 / GATE-5): bind the bespoke
    // hand-authored copy so the engine chrome is byte-identical, not
    // template-coincident.
    editSaveLabel: RACK_EDIT_SAVE_LABEL,
    editSaveDraftLabel: RACK_EDIT_SAVE_DRAFT_LABEL,
    editDeleteLabel: RACK_EDIT_DELETE_LABEL,
    deleteConfirmTitle: RACK_DELETE_CONFIRM_TITLE,
    deleteConfirmMessage: RACK_DELETE_CONFIRM_MESSAGE,
    editAllListLabel: RACK_EDIT_ALL_RACKS_LABEL,
    editDiscardTitle: RACK_DISCARD_UNSAVED_TITLE,
    editDiscardMessage: RACK_DISCARD_UNSAVED_MESSAGE,
    editDiscardConfirmLabel: RACK_DISCARD_UNSAVED_DISCARD_LABEL,
    editDiscardKeepEditingLabel: RACK_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
    newInstanceChromeTitle: 'New rack',
  },
  cardinality: 'many',
  fields: RACKS_DESCRIPTOR_FIELDS,
  permissionRef: { rbacAnchorDataField: 'rackPlatform' },
  summaryOrder: RACK_FORM_SUMMARY_FIELD_ORDER.map(field => field.dataField),
  pdfLayout: {
    stepId: 'rack-power',
    anchor: 'Rack Information',
    perUnitFieldGroupKeys: ['rackInfoFields', 'pduInfoFields'],
  },
  // Racks has NO request-level shared fields — every field, including Additional
  // Information, is per-unit.
  sharedFieldKeys: [],
  attachmentsEnabled: true,
  attachmentBaseSectionIds: [
    SECTION_IDS.RACK_CONFIGURATIONS,
    SECTION_IDS.RACK_POWER_INFO,
    SECTION_IDS.RACKS_ADDITIONAL,
  ],
  // Editor card partition (#997 Story 6.3) — mirrors the bespoke
  // `RackEditFormContent` 2-field-card layout: Card 1 "Racks" fuses the
  // rackInformation + specs groups; Card 2 holds the rackElectrical (PDU) group.
  // The additionalInformation group is the Additional Information card
  // (`additionalInfoField`), not a field-card here.
  editorCards: [
    {
      baseSectionId: SECTION_IDS.RACK_CONFIGURATIONS,
      title: 'Racks',
      fieldGroupKeys: ['rackInformation', 'specs'],
    },
    {
      baseSectionId: SECTION_IDS.RACK_POWER_INFO,
      title: 'Rack Power Information',
      fieldGroupKeys: ['rackElectrical'],
    },
  ],
  listColumns: RACKS_LIST_COLUMNS,
  additionalInfoField: 'rackAdditionalRackInformation',
  offlineBinding: {
    restResource: 'racks',
    // Racks has the proven delegating flush shim (Story 6.2/6.3), so it opts
    // into the engine save/flush path explicitly (#997 Story 6.4 — replaces the
    // former `restResource === 'racks'` magic-string gate).
    delegatedFlush: true,
    reorderIdsBodyKey: 'rackIds',
    persistedInstancesKey: 'rackConfigurations',
    instanceIdOpField: 'rackId',
    reorderIdsOpField: 'rackIds',
  },
  dbBinding: {
    table: 'request_racks',
    columns: RACKS_DESCRIPTOR_DB_COLUMNS,
  },
};
